import type { ChartOption, TooltipSpec } from "./chart-option.js"
import { ink, surface, type ThemeMode } from "../palette.js"
import { CHART_TOOLTIP_CSS, FONT_STACK } from "../brand.js"
import { formatValue } from "../format.js"
import type { VizModel } from "../tile-model.js"

/** Gridlines and axes are background, not content — 1px dashed and very pale, so they do not compete with the data for ink */
const axisCommon = (mode: ThemeMode) => {
  const c = ink(mode)
  return {
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: c.secondary, fontSize: 12, hideOverlap: true },
    splitLine: { show: true, lineStyle: { color: c.gridline, width: 1, type: "dashed" as const } },
  }
}

const CATEGORY_LABEL_MAX = 120

export type BaseOptions = {
  mode: ThemeMode
  model: VizModel
  horizontal?: boolean
  legendPosition?: "bottom" | "right"
}

/** The skeleton has no series — each chart type supplies its own. Spelled out in the types so that forgetting them does not still compile */
const LEGEND_WIDTH = 110

export function baseOption({
  mode,
  model,
  horizontal = false,
  legendPosition = "bottom",
}: BaseOptions): Omit<ChartOption, "series"> {
  const c = ink(mode)
  const legendVisible = model.presentation.legend

  const categoryAxis = {
    type: "category" as const,
    data: model.categories,
    boundaryGap: true,
    ...axisCommon(mode),
    axisLabel: {
      ...axisCommon(mode).axisLabel,
      ...(horizontal ? { width: CATEGORY_LABEL_MAX, overflow: "truncate" as const, ellipsis: "…" } : {}),
    },
    splitLine: { show: false },
  }

  const valueAxis = {
    type: "value" as const,
    ...axisCommon(mode),
    axisLabel: {
      color: c.secondary,
      fontSize: 12,
      formatter: (v: number) => formatValue(v, { valueType: model.unit, context: "axis" }),
    },
  }

  return {
    backgroundColor: "transparent",
    animationDuration: 240,
    textStyle: {
      color: c.primary,
      fontFamily: FONT_STACK,
    },
    grid: {
      top: legendVisible && legendPosition === "bottom" ? 36 : 16,
      right: legendVisible && legendPosition === "right" ? LEGEND_WIDTH + 16 : 16,
      bottom: 8,
      left: 8,
      containLabel: true,
    },
    legend: legendVisible
      ? {
          type: "scroll",
          itemGap: legendPosition === "right" ? 10 : 16,
          itemWidth: 12,
          itemHeight: 12,
          textStyle: { color: c.secondary, fontSize: 12 },
          inactiveColor: c.muted,
          ...(legendPosition === "right"
            ? { orient: "vertical" as const, right: 0, top: "middle" as const, width: LEGEND_WIDTH }
            : { top: 4, left: 0 }),
        }
      : { show: false },
    xAxis: horizontal ? valueAxis : categoryAxis,
    yAxis: horizontal ? categoryAxis : valueAxis,
  }
}

export function tooltipFor(mode: ThemeMode, model: VizModel, trigger: "axis" | "item"): TooltipSpec {
  const c = ink(mode)

  return {
    trigger,
    backgroundColor: surface(mode),
    borderColor: c.gridline,
    borderWidth: 1,
    padding: [8, 10],
    textStyle: { color: c.primary, fontSize: 12 },
    extraCssText: CHART_TOOLTIP_CSS,
    ...(trigger === "axis"
      ? {
          axisPointer: { type: "line" as const, lineStyle: { color: c.baseline, width: 1 }, snap: true },
        }
      : {}),
    formatter: (raw: unknown) => {
      const items = (Array.isArray(raw) ? raw : [raw]) as {
        seriesName?: string
        axisValueLabel?: string
        name?: string
        value?: number | null
        color?: string
      }[]
      if (items.length === 0) return ""

      const head = items[0]!.axisValueLabel ?? items[0]!.name ?? ""
      const lines = items
        .filter((it) => it.value != null)
        .map((it) => {
          const series = model.series.find((s) => s.name === it.seriesName)
          const value = formatValue(it.value, {
            valueType: series?.unit ?? model.unit,
            context: "label",
          })
          const mark = `<span style="display:inline-block;width:12px;height:2px;background:${it.color};vertical-align:middle;margin-right:6px"></span>`
          const badge = series?.approximate
            ? `<span style="color:${c.muted};font-size:11px;margin-left:4px">approx.</span>`
            : ""
          const stale = series?.state === "stale"
            ? `<span style="color:${c.muted};font-size:11px;margin-left:4px">data may be stale</span>`
            : ""
          return `<div style="display:flex;align-items:center;gap:8px;margin-top:2px">
            <span>${mark}</span>
            <strong style="color:${c.primary};font-variant-numeric:tabular-nums">${value}</strong>
            <span style="color:${c.secondary}">${it.seriesName ?? ""}</span>${badge}${stale}
          </div>`
        })

      if (lines.length === 0)
        return `<div style="color:${c.secondary};font-size:12px">${head} — no data</div>`

      return `<div style="color:${c.secondary};font-size:11px;margin-bottom:2px">${head}</div>${lines.join("")}`
    },
  }
}

export const STALE_OPACITY = 0.6
